import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout/app-layout";

console.log("App.tsx loaded, AppLayout imported:", typeof AppLayout);
import NotFound from "@/pages/not-found";
import DashboardSimple from "@/pages/dashboard-simple";
import Dashboard from "@/pages/dashboard";
import DownloadPage from "@/components/download-page";
import Domains from "@/pages/domains";
import Applications from "@/pages/applications";
import Trackers from "@/pages/trackers";
import Scripts from "@/pages/scripts";
import ChangeHistory from "@/pages/change-history";
import CSP from "@/pages/csp";
import ScheduledScans from "@/pages/scheduled-scans";
import Settings from "@/pages/settings";
import SignIn from "@/pages/signin";
import SignUp from "@/pages/signup";
import AuthSuccess from "@/pages/auth-success";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ScanReport from "@/pages/scan-report";
import ThreatIntelligence from "@/pages/threat-intelligence";
import SensitiveActions from "@/pages/sensitive-actions";
import UserInputs from "@/pages/user-inputs";
import RequestParameters from "@/pages/request-parameters";
import Notifications from "@/pages/notifications";
import SendReport from "@/pages/sendreport";
import NetworkAnalysis from "@/pages/network-analysis";
import TestEmail from "@/pages/test-email";
import UserManagement from "@/pages/user-management";
import RealTimeCompliance from "@/pages/compliance";
import MFASetup from "@/pages/mfa-setup";
import Subscription from "@/pages/subscription";
import WeakTLSCiphers from "@/pages/weak-tls-ciphers";
import PrivacyValidation from "@/pages/privacy-validation";
import TermsPage from "@/pages/terms";
import PrivacyPolicyPage from "@/pages/privacy-policy";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <SignIn />;
  }
  
  return <>{children}</>;
}

function Router() {
  const { isAuthenticated, isLoading } = useAuth();
  
  return (
    <Switch>
      {/* Public routes - only accessible when NOT authenticated */}
      <Route path="/signin">
        {isAuthenticated ? (
          <AppLayout>
            <Dashboard />
          </AppLayout>
        ) : <SignIn />}
      </Route>
      <Route path="/signup">
        {isAuthenticated ? (
          <AppLayout>
            <Dashboard />
          </AppLayout>
        ) : <SignUp />}
      </Route>
      <Route path="/auth/success" component={AuthSuccess} />
      <Route path="/forgot-password">
        {isAuthenticated ? (
          <AppLayout>
            <Dashboard />
          </AppLayout>
        ) : <ForgotPassword />}
      </Route>
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/download" component={DownloadPage} />
      <Route path="/terms" component={TermsPage} />
      <Route path="/privacy" component={PrivacyPolicyPage} />
      
      {/* Default route - redirect to login or dashboard */}
      <Route path="/">
        {isAuthenticated ? (
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        ) : (
          <SignIn />
        )}
      </Route>
      <Route path="/domains">
        <ProtectedRoute>
          <AppLayout>
            <Domains />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/applications">
        <ProtectedRoute>
          <AppLayout>
            <Applications />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/trackers">
        <ProtectedRoute>
          <AppLayout>
            <Trackers />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/scripts">
        <ProtectedRoute>
          <AppLayout>
            <Scripts />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/change-history">
        <ProtectedRoute>
          <AppLayout>
            <ChangeHistory />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/csp">
        <ProtectedRoute>
          <AppLayout>
            <CSP />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/weak-tls-ciphers">
        <ProtectedRoute>
          <AppLayout>
            <WeakTLSCiphers />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/compliance">
        <ProtectedRoute>
          <AppLayout>
            <RealTimeCompliance />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/privacy-validation">
        <ProtectedRoute>
          <AppLayout>
            <PrivacyValidation />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/scheduled-scans">
        <ProtectedRoute>
          <AppLayout>
            <ScheduledScans />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/settings">
        <ProtectedRoute>
          <AppLayout>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/mfa-setup">
        <ProtectedRoute>
          <AppLayout>
            <MFASetup />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/subscription">
        <ProtectedRoute>
          <AppLayout>
            <Subscription />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/subscription/success">
        <ProtectedRoute>
          <AppLayout>
            <Subscription />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/scans">
        <ProtectedRoute>
          <AppLayout>
            <ScheduledScans />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/scan/:id">
        <ProtectedRoute>
          <AppLayout>
            <ScanReport />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/threat-intelligence">
        <ProtectedRoute>
          <AppLayout>
            <ThreatIntelligence />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/network-analysis">
        <ProtectedRoute>
          <AppLayout>
            <NetworkAnalysis />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/sensitive-actions">
        <ProtectedRoute>
          <AppLayout>
            <SensitiveActions />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/user-inputs">
        <ProtectedRoute>
          <AppLayout>
            <UserInputs />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/request-parameters">
        <ProtectedRoute>
          <AppLayout>
            <RequestParameters />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/notifications">
        <ProtectedRoute>
          <AppLayout>
            <Notifications />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/sendreport">
        <ProtectedRoute>
          <AppLayout>
            <SendReport />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/test-email">
        <ProtectedRoute>
          <AppLayout>
            <TestEmail />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/user-management">
        <ProtectedRoute>
          <AppLayout>
            <UserManagement />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/monitoring">
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      
      {/* 404 route */}
      <Route>
        <ProtectedRoute>
          <AppLayout>
            <NotFound />
          </AppLayout>
        </ProtectedRoute>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider defaultTheme="light" storageKey="pci-scanner-theme">
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
